//@author: devin
import { NgTemplateOutlet } from "@angular/common";
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    input,
    numberAttribute,
    resource,
    signal,
    TemplateRef,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { NgbPopover, PlacementArray } from "@ng-bootstrap/ng-bootstrap";
import { Throttle } from "../../decorators/debounce.decorator";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Item } from "../../pojo/Item";
import { NameService } from "../../services/name.service";
import { OlsService } from "../../services/ols.service";
import { StateService } from "../../services/state.service";
import { TooltipResponse, TooltipService, TooltipServiceParams } from "../../services/tooltip.service";
import { UserprefService } from "../../services/userpref.service";
import { AngBridgeUtil } from "../../util/ang.bridge.util";
import { S25Util } from "../../util/s25-util";
import { ContextMenuModule } from "../s25-context-menu/context.menu.module";
import { ContextMenuService } from "../s25-context-menu/context.menu.service";
import { PopoverComponent } from "../s25-popover/popover.component";
import { PopoverModule } from "../s25-popover/popover.module";
import { S25ContactTooltip } from "./item-object/s25.item.contact.component";
import { S25EventTooltip } from "./item-object/s25.item.event.component";
import { S25OrganizationTooltip } from "./item-object/s25.item.organization.component";
import { S25ResourceTooltip } from "./item-object/s25.item.resource.component";
import { S25SpaceTooltip } from "./item-object/s25.item.space.component";
import { ItemUtil } from "./item.util";
import { SpreadsheetCell } from "../s25-spreadsheet/spreadsheet.service";
import { DropdownApi } from "../s25-dropdown/dropdown.api";

@TypeManagerDecorator("s25-item")
@Component({
    selector: "s25-item",
    host: {
        "[class]": "'s25-item--' + (itemType() ?? 'unknown')",
        "[class.s25-object-space]": "itemTypeId() === Item.Ids.Location",
        "[class.s25-object-resource]": "itemTypeId() === Item.Ids.Resource",
        "[class.s25-object-event]": "itemTypeId() === Item.Ids.Event",
        "[class.s25-object-org]": "itemTypeId() === Item.Ids.Organization",
        "[class.s25-object-contact]": "itemTypeId() === Item.Ids.Contact",
        "[class.no-link]": "!styleAsLink()",
    },
    template: `
        @if (!itemNameRq.isLoading() && !formalNameRq.isLoading()) {
            @if (!hidePopover()) {
                <s25-popover
                    [popoverTemplate]="popoverTemplate() || defaultItemPopoverTemplate"
                    [popoverClass]="popoverClasses() + ' s25-item__tooltip'"
                    [disabled]="isPrivate() || contextMenuOpen()"
                    [placement]="placement()"
                    [triggerTabIndex]="-1"
                    (shown)="onTooltipShown()"
                    (hidden)="onTooltipHidden()"
                    [isTooltip]="true"
                >
                    <ng-container [ngTemplateOutlet]="itemContent"></ng-container>
                </s25-popover>
                <div
                    [popoverClass]="popoverClasses() + ' s25-item__context-menu'"
                    [ngbPopover]="contextMenuContent"
                    [autoClose]="'outside'"
                    [container]="'body'"
                    [positionTarget]="(tooltipPopover()?.elementRef)!.nativeElement"
                    [placement]="placement() ?? ['auto']"
                    (shown)="contextMenuOpen.set(true)"
                    (hidden)="contextMenuOpen.set(false)"
                ></div>
            } @else {
                <ng-container [ngTemplateOutlet]="itemContent"></ng-container>
            }
        }

        <ng-template #itemContent>
            <div
                (click)="itemDetail($event)"
                (mouseup)="$event.button === 1 && itemDetail($event)"
                (keyup.enter)="itemDetail($any($event))"
                (contextmenu)="openContextMenu($event)"
                class="s25-item-holder"
                [class.s25-item-holder--with-icon]="includeTypeIcon()"
                [attr.aria-label]="ariaLabel() || providedItemName()"
                [attr.aria-describedby]="holderDescribedBy()"
                [attr.tabindex]="inactive() ? null : 0"
                [attr.role]="interactiveRole()"
            >
                @if (includeTypeIcon()) {
                    <div class="s25-item-type-icon-holder s25-item-type-icon-holder-{{ itemType() ?? 'unknown' }}">
                        <div class="{{ iconClass() }} s25-item-type-icon"></div>
                    </div>
                }
                <ng-content>
                    <span class="s25-item-name" [class.s25-item-link]="styleAsLink()">{{ itemName() }}</span>
                </ng-content>
                @if (!hideLabel() && itemLabel()) {
                    <span class="s25-item-label">{{ itemLabel() }}</span>
                }
            </div>
        </ng-template>
        <ng-template #defaultItemPopoverTemplate>
            <div class="qtip-content">
                @switch (itemTypeId()) {
                    @case (Item.Ids.Event) {
                        <s25-event-tooltip class="s25-item__tooltip-content" />
                    }
                    @case (Item.Ids.Organization) {
                        <s25-organization-tooltip class="s25-item__tooltip-content" />
                    }
                    @case (Item.Ids.Contact) {
                        <s25-contact-tooltip class="s25-item__tooltip-content" />
                    }
                    @case (Item.Ids.Location) {
                        <s25-space-tooltip class="s25-item__tooltip-content" />
                    }
                    @case (Item.Ids.Resource) {
                        <s25-resource-tooltip class="s25-item__tooltip-content" />
                    }
                }
            </div>
        </ng-template>
        <ng-template #contextMenuContent let-itemId="itemId" let-itemTypeId="itemTypeId" let-bean="bean">
            <s25-ng-context-menu
                [itemId]="itemId"
                [itemTypeId]="itemTypeId"
                [bean]="bean"
                (closed)="closeContextMenu()"
            />
        </ng-template>
    `,
    styles: `
        /* :host-context(#s25) for greater specificity to override external styling */
        :host-context(#s25) :host.no-link {
            cursor: auto !important;
        }

        .s25-item__tooltip-content {
            display: block;
            /* give it a bit of padding for while it's loading */
            padding: 0.5em;
        }
        ::ng-deep .s25-item__context-menu {
            --bs-popover-body-padding-x: 0;
            --bs-popover-body-padding-y: 0;

            .b-contextmenu {
                position: initial;
            }
        }
        ::ng-deep .s25-item__popover--event {
            --color--popover-highlight: var(--color--objects-event, #2b7c42);
        }
        ::ng-deep .s25-item__popover--organization {
            --color--popover-highlight: var(--color--objects-organization, #f1ca6f);
        }
        ::ng-deep .s25-item__popover--space {
            --color--popover-highlight: var(--color--objects-location, #12659d);
        }
        ::ng-deep .s25-item__popover--resource {
            --color--popover-highlight: var(--color--objects-resource, #e91e63);
        }
        ::ng-deep .s25-item__popover--contact {
            --color--popover-highlight: var(--color--objects-contact, #9a5ef7);
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgbPopover,
        S25ContactTooltip,
        S25EventTooltip,
        S25OrganizationTooltip,
        S25ResourceTooltip,
        S25SpaceTooltip,
        ContextMenuModule,
        PopoverModule,
        NgTemplateOutlet,
    ],
})
export class S25ItemComponent<TTooltipContext = unknown> implements SpreadsheetCell {
    readonly itemId = input.required({ transform: numberAttribute });
    readonly itemTypeId = input.required({
        transform: (itemTypeId: string | number): ItemUtil.SupportedItemType => {
            const parsed = numberAttribute(itemTypeId);
            if (!ItemUtil.SupportedItemTypes.includes(parsed)) {
                // TODO: throw an error?
                console.error("Unexpected itemTypeId provided to <s25-item>:", itemTypeId);
            }
            return parsed;
        },
    });
    readonly providedItemName = input<string | undefined>(undefined, { alias: "itemName" });
    readonly providedItemLabel = input<string | undefined>(undefined, { alias: "itemLabel" });
    readonly itemId2 = input(undefined, { transform: numberAttribute });
    readonly includeTypeIcon = input<boolean>(false);
    readonly typeIconClass = input<string>();
    readonly placement = input<PlacementArray>();
    readonly hidePopover = input(false);
    readonly popoverClass = input<string>();
    readonly inactive = input(false, { transform: booleanAttribute });
    readonly ariaLabel = input<string>();
    readonly newTab = input(false, { transform: booleanAttribute });
    readonly detailsOverrideFn = input<($event: KeyboardEvent | TouchEvent | MouseEvent) => Promise<unknown>>();
    readonly blockContextMenu = input<boolean>(false);
    readonly tooltipService =
        input<
            (params: S25ItemComponentTooltipParams<TTooltipContext>) => ReturnType<typeof TooltipService.getTooltip>
        >();
    readonly tooltipContext = input<TTooltipContext>();
    /**
     * legacy hideLabel behavior is to hide the label only if an itemName is provided.
     * that seems pretty unintentional and side-effecty to me, but I didn't want to track down
     * every existing usage and set hideLabel to true explicitly.
     * TODO: remove 'legacy'?
     */
    readonly hideLabel = input<boolean | "legacy">("legacy");
    readonly popoverTemplate = input<TemplateRef<any>>();
    readonly contextMenuBean = input<unknown>();

    public cd = inject(ChangeDetectorRef);

    readonly tooltipPopover = viewChild(PopoverComponent);
    readonly contextMenuPopover = viewChild(NgbPopover);
    readonly contextMenuOpen = signal(false);
    readonly tooltipDescribedById = signal<string | undefined>(undefined);

    readonly data = signal<
        | (TooltipResponse & {
              timeFormat?: string;
              dateFormat?: string;
          })
        | undefined
    >(undefined);
    readonly timeFormat = signal<string | undefined>(undefined);
    readonly dateFormat = signal<string | undefined>(undefined);
    readonly initPopOver = signal<boolean>(false);
    readonly requestingData = signal<boolean>(false);

    readonly popoverClasses = computed(() =>
        [this.popoverClass(), `s25-item__popover--${this.itemType() ?? "unknown"}`].filter(S25Util.isDefined).join(" "),
    );
    readonly isPrivate = computed(() => S25Util.isPrivate(this.itemName()));
    readonly iconClass = computed(() => this.typeIconClass() || ItemUtil.IconStyles[this.itemTypeId()]);
    readonly itemType = computed(() => S25ItemComponent.TypeMap[this.itemTypeId()]);
    readonly itemNameRq = this.makeNameResource({
        getProvidedName: () => this.providedItemName(),
        fetchFn: (params) => NameService.getNameDebounced([{ itemId: params.itemId, itemTypeId: params.itemTypeId }]),
    });
    readonly itemName = computed(() => (this.itemNameRq.hasValue() ? this.itemNameRq.value() : "Loading..."));
    readonly formalNameRq = this.makeNameResource({
        getProvidedName: () => this.providedItemLabel(),
        dontFetch: () => this.hideLabel() === true || (this.hideLabel() === "legacy" && !!this.providedItemName()),
        fetchFn: (params) => NameService.getFormalDebounced([{ itemId: params.itemId, itemTypeId: params.itemTypeId }]),
    });
    readonly itemLabel = computed(() => (this.formalNameRq.hasValue() ? this.formalNameRq.value() : undefined));
    readonly loading = computed(
        () => this.itemNameRq.isLoading() || this.formalNameRq.isLoading() || this.requestingData(),
    );
    readonly styleAsLink = computed(() => this.itemNameRq.hasValue() && !this.isPrivate());
    readonly interactiveRole = computed(() => (this.inactive() || this.isPrivate() ? null : "link"));
    readonly holderDescribedBy = computed(() =>
        this.hidePopover() || this.isPrivate() ? undefined : this.tooltipDescribedById(),
    );

    protected readonly Item = Item;

    constructor() {
        AngBridgeUtil.init(this);
    }

    private static TypeMap: { [key in ItemUtil.SupportedItemType]?: string } = {
        [Item.Ids.Event]: "event",
        [Item.Ids.Organization]: "organization",
        [Item.Ids.Contact]: "contact",
        [Item.Ids.Location]: "space",
        [Item.Ids.Resource]: "resource",
    };

    @Throttle(500, false) // No double-clicking!
    async itemDetail($event: KeyboardEvent | TouchEvent | MouseEvent, item?: { itemTypeId: Item.Id; itemId: number }) {
        const itemTypeId = item?.itemTypeId || this.itemTypeId();
        const itemId = item?.itemId || this.itemId();

        // isInactive or isPrivate, do nothing
        // note: isInactive, isPrivate, plainText should still fire parent event because we do not handle the workflow for them
        if (this.inactive() || this.isPrivate() || !itemTypeId) {
            //skip inactive and plain text type
            if (!(await OlsService.hasOlsOverride(<Item.Id>itemTypeId))) return;
        }

        if (this.detailsOverrideFn()) {
            return this.detailsOverrideFn()!($event);
        }

        // stop firing any parent 'click' events when we are clicked
        // http://stackoverflow.com/questions/20300866/angularjs-ng-click-stoppropagation
        // required by Event Details Occurrence panel collapsed reservation row click on s25-item should not expand the row
        $event?.stopPropagation?.();
        $event?.preventDefault?.();

        const newTab =
            this.newTab() ||
            $event?.ctrlKey ||
            $event?.metaKey ||
            ($event instanceof MouseEvent && ($event?.button === 1 || $event?.which === 2));
        if (!newTab) {
            void DropdownApi.close(document.body);
        }
        this.tooltipPopover()?.forceClose();
        StateService.gotoItem(
            {
                itemId: itemId,
                itemTypeId: itemTypeId,
            },
            newTab,
        );
    }

    openContextMenu(
        $event: KeyboardEvent | TouchEvent | MouseEvent,
        item?: { itemTypeId: ItemUtil.SupportedItemType; itemId: number },
    ) {
        if (this.isPrivate() || this.blockContextMenu()) {
            return;
        }

        $event?.stopPropagation?.();
        $event?.preventDefault?.();

        ContextMenuService.closeMenu();

        return this.contextMenuPopover()?.open({
            itemId: item?.itemId || this.itemId(),
            itemTypeId: item?.itemTypeId || this.itemTypeId(),
            bean: this.contextMenuBean(),
        });
    }

    closeContextMenu() {
        this.contextMenuPopover()?.close();
    }

    // TODO: load this upon a new <s25-popover (interacted)> that is not delayed?
    onTooltipShown() {
        let getTooltipData = TooltipService.getTooltip<TooltipResponse>;
        if (this.tooltipService()) {
            getTooltipData = (params) => this.tooltipService()!({ ...params, context: this.tooltipContext()! });
        }
        if (ItemUtil.SupportedItemTypes.includes(this.itemTypeId())) {
            this.requestingData.set(true);
            getTooltipData({ itemTypeId: this.itemTypeId(), itemId: this.itemId(), itemId2: this.itemId2() })
                .then((resp) => this.data.set(resp || ({} as any)))
                .finally(() => this.requestingData.set(false));
        }
        UserprefService.getS25Timeformat().then((resp) => this.timeFormat.set(resp));
        UserprefService.getS25Dateformat().then((resp) => this.dateFormat.set(resp));
        this.initPopOver.set(true);
        this.tooltipDescribedById.set(this.tooltipPopover()?.popoverElement()?.id);
    }

    onTooltipHidden() {
        this.tooltipDescribedById.set(undefined);
    }

    private makeNameResource({
        getProvidedName,
        fetchFn,
        dontFetch,
    }: {
        getProvidedName: () => string | undefined;
        fetchFn: (params: {
            itemId: number;
            itemTypeId: ItemUtil.SupportedItemType;
        }) => Promise<Record<string, string>>;
        dontFetch?: () => boolean;
    }) {
        return resource({
            params: ():
                | { existingName: string }
                | {
                      itemId: number;
                      itemTypeId: ItemUtil.SupportedItemType;
                  }
                | undefined => {
                if (dontFetch?.()) {
                    return undefined;
                }
                const providedName = getProvidedName();
                if (providedName) {
                    return { existingName: providedName };
                } else {
                    return {
                        itemId: this.itemId(),
                        itemTypeId: this.itemTypeId(),
                    };
                }
            },
            loader: async ({ params }) => {
                if ("existingName" in params) {
                    return Promise.resolve(params.existingName);
                } else {
                    return (await fetchFn(params))[
                        NameService.itemKey({
                            itemId: params.itemId,
                            itemTypeId: params.itemTypeId,
                        })
                    ];
                }
            },
        });
    }

    getSpreadsheetCellValue(): ReturnType<SpreadsheetCell["getSpreadsheetCellValue"]> {
        return this.itemName();
    }
}

export type S25ItemComponentTooltipParams<TContext> = TooltipServiceParams & { context: TContext };
